import React, { useEffect, useRef, useState } from "react";
import type { PremiumFeedback } from "../api/resources";

/**
 * The premium coaching in the combined Feedback view (feedback first, owner 2026-10-10): every sentence the coach says,
 * by position, with its voice clip, the moment the narration is voiced, a minute before the narrated video. The clips
 * step through on their own; browsers allow sound only after a tap, so it starts muted with "Tap for sound" (owner:
 * "just have them muted"), and after the tap each clip plays and the next follows.
 */
/** A sentence's cue key, as the live view names its cues (`${position}:${overlay}`); null without both. */
export const coachingKey = (it: { position: string | null; overlay?: string }) =>
  it.position && it.overlay ? `${it.position}:${it.overlay}` : null;

export default function PremiumCoaching({
  feedback,
  clipBase,
  activeKey,
  onSelect,
}: {
  feedback: PremiumFeedback;
  clipBase: string;
  /** LINKED to the live view above (owner, 2026-10-12: "clicking on the your coaching texts should make the feedback
   * part match"): the cue it is on — the highlight follows it — and the way to send it to a sentence. The live view
   * then does the speaking; this list plays no audio of its own, so the voice is never doubled. */
  activeKey?: string | null;
  onSelect?: (key: string) => void;
}) {
  const items = feedback.items ?? [];
  const linked = !!onSelect;
  const [at, setAt] = useState(0);
  const [sound, setSound] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const current = items[at];

  // Muted: the highlight steps on by each clip's length. With sound: the clip plays and its end steps on.
  useEffect(() => {
    if (linked || !current || sound) return;
    const t = window.setTimeout(() => setAt((i) => (i + 1) % Math.max(1, items.length)), Math.max(1500, (current.seconds || 3) * 1000));
    return () => window.clearTimeout(t);
  }, [at, sound, current, items.length]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !sound) return;
    a.muted = false;
    a.play?.()?.catch?.(() => undefined);
  }, [at, sound]);

  if (items.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="premium-coaching">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900">Your coaching</div>
        {!sound && !linked && (
          <button
            type="button"
            className="rounded-full bg-el-600 px-3 py-1 text-xs font-semibold text-white hover:bg-el-700"
            onClick={() => setSound(true)}
            data-testid="coaching-sound"
          >
            Tap for sound
          </button>
        )}
      </div>
      <ol className="space-y-1.5">
        {items.map((it, i) => {
          const key = coachingKey(it);
          const active = linked ? !!key && key === activeKey : i === at;
          return (
          <li
            key={`${it.fileId}-${i}`}
            className={`flex gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer ${active ? "bg-el-50 text-slate-900" : "text-slate-600"}`}
            data-testid="coaching-item"
            data-active={active ? "true" : "false"}
            onClick={() => {
              if (linked) {
                if (key) onSelect!(key);
              } else setAt(i);
            }}
          >
            {it.position && <span className="shrink-0 font-semibold text-el-700">{it.position}</span>}
            <span>{it.text}</span>
          </li>
          );
        })}
      </ol>
      {!linked && current?.audio && (
        <audio
          key={`${current.audio}-${at}`}
          ref={audioRef}
          src={`${clipBase}${current.audio}`}
          muted={!sound}
          autoPlay={sound}
          preload="auto"
          onEnded={() => setAt((i) => (i + 1 < items.length ? i + 1 : i))}
          data-testid="coaching-audio"
        />
      )}
    </div>
  );
}
